/**
 * Deep localisation of backend rows that carry keyed messages (aicio/i18n_keys.py `<field>_i18n`): every string
 * field with a `<field>_i18n` sibling — and every list of strings with a parallel `<field>_i18n` list — is
 * replaced by its rendering in the member's language. English, and fields without a key, are returned as they
 * came (the same object for English, so nothing re-renders).
 */
import { getMyCIOLanguage } from '@/lib/mycio/language'
import { serverText, serverTextList } from '@/lib/mycio/serverText'

function walk(value: unknown, language: string): unknown {
  if (Array.isArray(value)) return value.map((v) => walk(v, language))
  if (!value || typeof value !== 'object') return value
  const src = value as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(src)) {
    if (k.endsWith('_i18n')) { out[k] = v; continue }
    const keyed = src[`${k}_i18n`]
    if (keyed !== undefined && typeof v === 'string') out[k] = serverText(v, keyed, language)
    else if (keyed !== undefined && Array.isArray(v) && v.every((x) => typeof x === 'string')) out[k] = serverTextList(v as string[], keyed, language)
    else out[k] = walk(v, language)
  }
  return out
}

/** `value` with every keyed field rendered in `language` (deep). English returns `value` itself. */
export function localizeServerFields<T>(value: T, language: string | null | undefined): T {
  const code = getMyCIOLanguage(language).code
  if (code === 'en' || value == null) return value
  return walk(value, code) as T
}

/** Only the named top-level fields of `obj` localised (the rest untouched, same references). */
export function localizeServerKeys<T extends object>(obj: T, keys: readonly string[], language: string | null | undefined): T {
  const code = getMyCIOLanguage(language).code
  if (code === 'en') return obj
  const o = obj as Record<string, unknown>
  if (!keys.some((k) => o[k] != null)) return obj
  const out: Record<string, unknown> = { ...o }
  for (const k of keys) if (o[k] != null) out[k] = localizeServerFields(o[k], code)
  return out as T
}
